/**
 * Derives a compact, human-readable page key for the current route in the
 * format `ts.{surface}.{slug}`, suitable for use as a `utm_content` value or
 * a grouping key in the AIZEn Click Sources report.
 *
 * Format:  ts.{surface}.{slug}
 * Example: ts.blog.how-to-short-a-stock
 *          ts.news.market-update-july-2026
 *          ts.home
 *
 * Rules:
 *  - All lowercase, max 95 chars (GA4 truncates utm_content at 100 chars).
 *  - `surface` = first non-empty path segment (or "home" for "/").
 *  - `slug`    = remaining path segments joined with ".", URL-decoded, with
 *                trailing slashes stripped.
 *  - An optional `postId` (WordPress numeric ID) is exposed alongside the
 *    key so callers can forward it to click tracking without a second lookup.
 *
 * @param postId - Reactive or static WordPress post/page ID, when available.
 *                 Pass a Ref to get a reactive pageKey that updates when the
 *                 post ID resolves after async data load.
 */

const SITE = 'ts'
const MAX_LEN = 95

type MaybeRef<T> = T | Ref<T>

export function usePageKey(postId?: MaybeRef<string | number | null | undefined>) {
  const route = useRoute()

  const pageKey = computed<string>(() => {
    const segments = route.path
      .split('/')
      .map((s) => decodeURIComponent(s).toLowerCase().replace(/\s+/g, '-'))
      .filter(Boolean)

    if (!segments.length) return `${SITE}.home`

    const surface = segments[0]
    const rest    = segments.slice(1).join('.')
    const full    = rest ? `${SITE}.${surface}.${rest}` : `${SITE}.${surface}`

    return full.slice(0, MAX_LEN)
  })

  const resolvedPostId = computed<string>(() => {
    const raw = isRef(postId) ? postId.value : postId
    return raw != null ? String(raw) : ''
  })

  return { pageKey, postId: resolvedPostId }
}
